import {
  type WithdrawalAmountValidation,
  deductFlatFee,
  isAddressCompatibleWithChain,
  isAddressEquivalent,
  isTokenEquivalent,
  isWithdrawalAddressSupported,
  toWithdrawalAmountBaseUnit,
  validateWithdrawalAmount,
} from '@funkit/connect-core'

import type {
  WithdrawalFlowConfig,
  WithdrawalToken,
} from '../flow/withdrawalFlowConfig'

// Everything the crypto withdrawal form derives from what the user has typed.
// Pure and synchronous — the async verdicts (risk screening, the quote) are
// layered on by the caller.

/**
 * The one thing the user has left to fix, named so the submit button can say it.
 *
 * Nothing left to fix is `undefined`, not a `ready` member: these are what the
 * typed form is missing, and that is not the same as being submittable — the
 * async verdicts (risk screening, a settled quote) decide that.
 */
export type WithdrawCtaBlocker =
  | 'enter-recipient'
  | 'unsupported-recipient'
  | 'recipient-same-as-source'
  | 'max-exceeded'
  | 'enter-amount'
  | 'select-destination'
  | 'invalid-for-chain'

/**
 * Amount limits, resolved from the host's callbacks.
 *
 * `sourceTokenUsdPrice` of 0 means unknown, not free: the core validator reads
 * it as "no price" and leaves the USD floor and ceiling inert, while the
 * per-token minimum and the balance check still apply. Sources whose price the
 * API cannot resolve (Lighter's index-addressed L2 assets) run that way.
 */
export function validateAmount({
  config,
  sourceToken,
  sourceTokenUsdPrice,
  sourceTokenDecimals,
  amount,
  balance,
}: {
  config: Pick<
    WithdrawalFlowConfig,
    | 'getMinWithdrawalAmount'
    | 'getMinWithdrawalUSD'
    | 'getMaxWithdrawalUSD'
    | 'getMaxWithdrawalAmount'
  >
  sourceToken: WithdrawalToken
  /** Spot USD price of one source token; 0 when unknown. */
  sourceTokenUsdPrice?: number
  /** Fraction digits the source carries; caps how fine the USD floor gets. */
  sourceTokenDecimals?: number
  amount: string
  balance: string | undefined
}): WithdrawalAmountValidation {
  const result = validateWithdrawalAmount({
    withdrawalInputAmount: amount,
    sourceTokenUsdPrice: sourceTokenUsdPrice ?? 0,
    sourceTokenDecimals,
    sourceTokenBalance: balance ?? '0',
    perTokenMin: config.getMinWithdrawalAmount?.({ sourceToken }),
    perTokenMax: config.getMaxWithdrawalAmount?.({ sourceToken }),
    usdMin: config.getMinWithdrawalUSD?.({ sourceToken }) ?? 0.01,
    usdMax: config.getMaxWithdrawalUSD?.({ sourceToken }),
  })

  // Nothing to withdraw is not a valid amount. The core validator compares
  // `Number(amount)`, and both `NaN` (from `'.'`) and 0 fail every comparison,
  // so they come back valid — the $0.01 floor catches 0 only once a price is
  // known (see above).
  const parsed = Number(amount)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return { ...result, isWithdrawAmountValid: false }
  }

  // An unknown balance is not a zero balance. Coalescing to '0' above keeps the
  // core validator happy, but judging the amount against it would reject every
  // withdrawal a host that supplies no balance callback ever attempts. We
  // cannot know, so we do not block — an actually-insufficient balance is
  // caught by the quote, which fails with a real reason.
  if (balance == null) {
    return {
      ...result,
      isWithdrawAmountAboveBalance: false,
      isWithdrawAmountValid:
        !result.isWithdrawAmountTooLow && !result.isWithdrawAmountTooHigh,
    }
  }
  return result
}

/**
 * A same-chain, same-token transfer back to the sending wallet. Relay rejects
 * these with a generic error, so name the reason before quoting.
 *
 * Relay rail only: on a venue rail the balance sits in the venue's account, not
 * the wallet, so the same combination is a real withdrawal.
 */
export function isNoOpSelfWithdrawal({
  recipientAddress,
  walletAddress,
  sourceToken,
  destinationToken,
  isVenueRail,
}: {
  recipientAddress: string
  walletAddress: string
  sourceToken: WithdrawalToken
  destinationToken: WithdrawalToken | undefined
  isVenueRail: boolean
}): boolean {
  if (isVenueRail || !destinationToken) {
    return false
  }
  return (
    isAddressEquivalent({
      firstAddress: recipientAddress,
      secondAddress: walletAddress,
    }) &&
    isTokenEquivalent({
      firstTokenChainId: sourceToken.chainId,
      firstTokenAddress: sourceToken.address,
      secondTokenChainId: destinationToken.chainId,
      secondTokenAddress: destinationToken.address,
    })
  )
}

/**
 * Order is a faithful port of web's `getWithdrawButtonText`: the most specific
 * blocker wins, so the user is told the one thing to fix next rather than the
 * first thing that happens to fail.
 */
export function getWithdrawCtaBlocker({
  recipientAddress,
  isRecipientBlacklisted,
  isRecipientSameAsSource,
  amount,
  amountValidation,
  destinationToken,
}: {
  recipientAddress: string
  isRecipientBlacklisted: boolean
  isRecipientSameAsSource: boolean
  amount: string
  amountValidation: WithdrawalAmountValidation
  destinationToken: WithdrawalToken | undefined
}): WithdrawCtaBlocker | undefined {
  if (!isWithdrawalAddressSupported(recipientAddress)) {
    return 'enter-recipient'
  }
  // Sending to a token contract burns the funds.
  if (isRecipientBlacklisted) {
    return 'unsupported-recipient'
  }
  if (isRecipientSameAsSource) {
    return 'recipient-same-as-source'
  }
  if (amountValidation.isWithdrawAmountTooHigh) {
    return 'max-exceeded'
  }
  if (!amount || !amountValidation.isWithdrawAmountValid) {
    return 'enter-amount'
  }
  if (!destinationToken) {
    return 'select-destination'
  }
  if (
    !isAddressCompatibleWithChain(
      recipientAddress,
      Number(destinationToken.chainId),
    )
  ) {
    return 'invalid-for-chain'
  }
  return undefined
}

/**
 * Whether the venue's flat fee leaves nothing for the quote to price.
 *
 * - Two ways to leave nothing: the fee meets or exceeds the amount, or the
 *   remainder is under one base unit of the source token — `parseUnits` trims
 *   the extra digits, so a positive remainder still reaches the quote as 0.
 * - Undefined `decimals` means the read hasn't landed. The quote can't run
 *   without it either, so the fee-only verdict stands until it does.
 */
export function isAmountBelowQuoteFee({
  amount,
  feeTokenAmount,
  decimals,
}: {
  amount: string
  feeTokenAmount: number
  decimals: number | undefined
}): boolean {
  if (feeTokenAmount <= 0 || !amount) {
    return false
  }
  const net = deductFlatFee(amount, feeTokenAmount)
  if (Number(net) <= 0) {
    return true
  }
  if (decimals == null) {
    return false
  }
  try {
    return toWithdrawalAmountBaseUnit(net, decimals) <= 0n
  } catch {
    // An amount `parseUnits` rejects can't be quoted either.
    return true
  }
}
